import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { UseFormSetError } from 'react-hook-form';

import { FormType } from '~/pages/pay/ui/PayPage.scheme';
import { customCalculate } from '~/shared/api/requests/customCalculate/customCalculate';
import { CustomCalculateResponseType } from '~/shared/api/types/catalog';

import {
  PromocodeErrorCode,
  PromocodeErrorResponse,
} from '../lib/promocode-errors';
import { useLocalization } from '../localization/localization';
import { DEFAULT_CURRENCY } from '../utils/constants/defaultValues';

type UseSteamCalculateOptions = {
  productId?: string;
  amount?: number;
  appliedPromocode?: string;
  setError: UseFormSetError<FormType>;
};

export const useSteamCalculate = ({
  productId,
  amount,
  appliedPromocode,
  setError,
}: UseSteamCalculateOptions) => {
  const { t } = useLocalization();
  const [amounts, setAmounts] = useState<CustomCalculateResponseType | null>(
    null,
  );

  const queryKey = useMemo(
    () => ['customCalculate', productId, amount],
    [productId, amount],
  );

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (!productId || !amount || amount <= 0) {
        return null;
      }

      const data = await customCalculate({
        amount: {
          currency: DEFAULT_CURRENCY,
          value: amount,
        },
        productId,
        ...(appliedPromocode ? { promocode: appliedPromocode } : {}),
      });

      return data as CustomCalculateResponseType;
    },
    enabled: !!productId && !!amount && amount > 0,
  });

  useEffect(() => {
    if (!productId || !amount || amount <= 0) {
      setAmounts(null);
      return;
    }

    if (query.isSuccess) {
      setAmounts(query.data ?? null);
    }
  }, [amount, productId, query.data, query.dataUpdatedAt, query.isSuccess]);

  const getPromocodeErrorMessage = (code: string): string => {
    const ERROR_MESSAGES: Record<PromocodeErrorCode, string> = {
      [PromocodeErrorCode.ALREADY_USED]: t('promocode_already_used'),
      [PromocodeErrorCode.INACTIVE]: t('promocode_inactive'),
      [PromocodeErrorCode.VALIDATION_FAILED]: t('promocode_validation_failed'),
    };

    return (
      ERROR_MESSAGES[code as PromocodeErrorCode] || t('promocode_unknown_error')
    );
  };

  const promocodeMutation = useMutation({
    mutationFn: async (code: string) => {
      if (!productId || !amount || amount <= 0) {
        throw new Error('Missing required data');
      }

      const data = await customCalculate({
        amount: {
          currency: DEFAULT_CURRENCY,
          value: amount,
        },
        productId,
        ...(code ? { promocode: code } : {}),
      });

      return data as CustomCalculateResponseType;
    },
    onMutate: () => {
      setError('promocode', { message: undefined });
    },
    onSuccess: (data) => {
      setAmounts(data);
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        const errorData = error.response?.data?.error as
          | PromocodeErrorResponse
          | undefined;

        const message = errorData?.code
          ? getPromocodeErrorMessage(errorData.code)
          : errorData?.message;

        if (message) {
          setError('promocode', { message });
          return;
        }
      }

      setError('promocode', { message: t('network_error') });
    },
  });

  return {
    amounts,
    isCalculating: query.isFetching,
    isApplyingPromocode: promocodeMutation.isPending,
    applyPromocodeMutation: promocodeMutation,
    refetch: query.refetch,
  };
};
